// Phase SA-8 — Pure helpers for autopsy history filtering, stats,
// and CSV export.

/** One row from public.slip_autopsies (SA-2 migration). */
export interface SlipAutopsyRow {
  autopsy_id: string;
  queue_id: string | null;
  user_id: string;
  slip_date: string | null;
  bookmaker: string | null;
  outcome: "won" | "lost" | "partial" | "void" | "unknown" | null;
  stake: number | null;
  returns: number | null;
  profit_loss: number | null;
  leg_count: number;
  combined_odds: number | null;
  process_correct_count: number;
  below_threshold_count: number;
  model_error_count: number;
  void_count: number;
  no_data_count: number;
  overall_process_verdict: "SOUND" | "MIXED" | "RISKY" | null;
  autopsy_summary: string | null;
  created_at: string;
}

export type OutcomeFilter =
  | "all"
  | "won"
  | "lost"
  | "partial"
  | "void"
  | "unknown";

export type VerdictFilter =
  | "all"
  | "process_correct"
  | "below_threshold"
  | "model_error";

export type TimeRangeFilter = "all" | "7d" | "30d" | "90d";

export interface HistoryFilters {
  outcome: OutcomeFilter;
  verdict: VerdictFilter;
  timeRange: TimeRangeFilter;
}

export const DEFAULT_FILTERS: HistoryFilters = {
  outcome: "all",
  verdict: "all",
  timeRange: "all",
};

/** Pure: does `row` match the current filter set? */
export function rowMatches(
  row: SlipAutopsyRow,
  filters: HistoryFilters,
  /** Wallclock now for time-range checks — injectable for tests. */
  now: number = Date.now(),
): boolean {
  if (filters.outcome !== "all" && row.outcome !== filters.outcome) return false;

  if (filters.verdict !== "all") {
    const key =
      filters.verdict === "process_correct"
        ? "process_correct_count"
        : filters.verdict === "below_threshold"
          ? "below_threshold_count"
          : "model_error_count";
    if ((row[key] ?? 0) === 0) return false;
  }

  if (filters.timeRange !== "all") {
    const days =
      filters.timeRange === "7d" ? 7 : filters.timeRange === "30d" ? 30 : 90;
    const cutoff = now - days * 24 * 60 * 60 * 1000;
    const created = new Date(row.created_at).getTime();
    if (!Number.isFinite(created) || created < cutoff) return false;
  }

  return true;
}

/** Pure: filter + sort a row array (newest first). */
export function filterRows(
  rows: ReadonlyArray<SlipAutopsyRow>,
  filters: HistoryFilters,
  now: number = Date.now(),
): SlipAutopsyRow[] {
  return rows
    .filter((r) => rowMatches(r, filters, now))
    .slice()
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
}

export interface RollingStats {
  windowDays: number;
  slips: number;
  legs: number;
  win_rate_pct: number | null;
  process_accuracy_pct: number | null;
}

/** Pure: aggregate rolling stats across rows in the last `days`. */
export function aggregateStats(
  rows: ReadonlyArray<SlipAutopsyRow>,
  days: number = 30,
  now: number = Date.now(),
): RollingStats {
  const cutoff = now - days * 24 * 60 * 60 * 1000;
  const inWindow = rows.filter((r) => {
    const t = new Date(r.created_at).getTime();
    return Number.isFinite(t) && t >= cutoff;
  });

  const slips = inWindow.length;
  const legs = inWindow.reduce((s, r) => s + (r.leg_count ?? 0), 0);

  // Win rate = won slips / settled slips. 'partial' counted toward
  // settled but not toward wins. 'void' + 'unknown' excluded from
  // both numerator and denominator (a void slip is not a result).
  const settled = inWindow.filter(
    (r) => r.outcome === "won" || r.outcome === "lost" || r.outcome === "partial",
  );
  const wins = settled.filter((r) => r.outcome === "won").length;
  const winRate = settled.length === 0 ? null : (wins / settled.length) * 100;

  // Process accuracy = process_correct / (process_correct +
  //                                       below_threshold +
  //                                       model_error)
  // Excludes void + no-data legs from both sides.
  let pc = 0;
  let bt = 0;
  let me = 0;
  for (const r of inWindow) {
    pc += r.process_correct_count ?? 0;
    bt += r.below_threshold_count ?? 0;
    me += r.model_error_count ?? 0;
  }
  const total = pc + bt + me;
  const procAcc = total === 0 ? null : (pc / total) * 100;

  return {
    windowDays: days,
    slips,
    legs,
    win_rate_pct: winRate,
    process_accuracy_pct: procAcc,
  };
}

/** Pure: convert filtered rows into a CSV string. Brief §06.1
 * lists the columns. Header row included. */
export function toCSV(rows: ReadonlyArray<SlipAutopsyRow>): string {
  const headers = [
    "slip_date",
    "outcome",
    "leg_count",
    "combined_odds",
    "stake",
    "returns",
    "profit_loss",
    "process_correct_count",
    "below_threshold_count",
    "model_error_count",
    "overall_process_verdict",
    "bookmaker",
  ];
  const escape = (v: unknown): string => {
    if (v == null) return "";
    const s = String(v);
    if (s.includes(",") || s.includes('"') || s.includes("\n")) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const lines: string[] = [headers.join(",")];
  for (const r of rows) {
    lines.push(
      [
        r.slip_date,
        r.outcome,
        r.leg_count,
        r.combined_odds,
        r.stake,
        r.returns,
        r.profit_loss,
        r.process_correct_count,
        r.below_threshold_count,
        r.model_error_count,
        r.overall_process_verdict,
        r.bookmaker,
      ]
        .map(escape)
        .join(","),
    );
  }
  return lines.join("\n");
}

/** Format a slip date for the history row's left column. Defaults
 * to MMM DD (e.g. "May 12"). */
export function formatSlipDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "2-digit" });
}

/** Format combined odds with the × suffix. */
export function formatOdds(odds: number | null): string {
  if (odds == null || !Number.isFinite(odds)) return "—";
  return `${odds.toFixed(2)}×`;
}

/** Build the per-row verdict summary pieces. SA-8 row renders each
 * piece as a separate span with data-tone so the colours match the
 * verdict palette from SA-3. */
export function verdictSummaryPieces(row: SlipAutopsyRow): Array<{
  label: string;
  tone: "green" | "amber" | "red";
}> {
  const out: Array<{ label: string; tone: "green" | "amber" | "red" }> = [];
  if (row.process_correct_count > 0) {
    out.push({ label: `${row.process_correct_count} CORRECT`, tone: "green" });
  }
  if (row.below_threshold_count > 0) {
    out.push({ label: `${row.below_threshold_count} BELOW`, tone: "amber" });
  }
  if (row.model_error_count > 0) {
    out.push({ label: `${row.model_error_count} ERROR`, tone: "red" });
  }
  return out;
}
